import { quarantineCustomerOwnedSessionStorage } from "@/app/booking/core/customerOwnedSessionStorageBarrier";
import {
  captureCustomerOwnedSessionStorageMutationProof,
  commitCustomerOwnedSessionStorageMutation,
  customerOwnedSessionStorageMutationProofIsCurrent,
  readCustomerOwnedSessionStorageRawValueFromCapturedReader,
  readCustomerOwnedSessionStorageRawValueStable,
} from "@/app/booking/core/customerOwnedSessionStorageMutationCoordinator";
import { createVersionedSessionDraftStore } from "@/app/booking/core/versionedSessionDraftStore";
import {
  beginSpecialistLocationGatewayCustomerOwnedWrite,
  completeSpecialistLocationGatewayCustomerOwnedWrite,
  specialistLocationGatewayCustomerOwnedWriteIsCurrent,
  specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent,
} from "../terminalWriteCoordinator";
import {
  SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_MAX_TTL_MS,
  isSpecialistLocationGatewayConfiguredServicesCartFresh,
  isSpecialistLocationGatewayServicesCartRevisionSuccessor,
  validateSpecialistLocationGatewayConfiguredServicesCart,
  type SpecialistLocationGatewayConfiguredServicesCartV1,
} from "./configuredServicesCartIntent";
import { isSpecialistLocationGatewayIdentifier } from "../contract";

export const SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_STORAGE_KEY =
  "volera:specialist-location-gateway:configured-services-carts:v1";
export const SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_MAX_ENTRIES = 16;

const cartStore =
  createVersionedSessionDraftStore<SpecialistLocationGatewayConfiguredServicesCartV1>(
    {
      storageKey: SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_STORAGE_KEY,
      storeKind: "specialist-location-gateway-configured-services-carts",
      maxEntries: SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_MAX_ENTRIES,
      maxAgeMs: SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_MAX_TTL_MS,
      maxSerializedCharacters: 1_200_000,
      validate: validateSpecialistLocationGatewayConfiguredServicesCart,
    },
  );

const CART_STORE_KIND =
  "specialist-location-gateway-configured-services-carts";
const CART_STORE_VERSION = 1 as const;
const CART_STORE_MAX_SERIALIZED_CHARACTERS = 1_200_000;
const CART_STORE_MAX_SAVED_AT_CLOCK_SKEW_MS = 5 * 60 * 1_000;

interface PersistedCartEntry {
  draftId: string;
  savedAtEpochMs: number;
  draft: SpecialistLocationGatewayConfiguredServicesCartV1;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(
  value: Record<string, unknown>,
  expected: readonly string[],
) {
  const keys = Object.keys(value);
  return (
    keys.length === expected.length &&
    keys.every((key) => expected.includes(key))
  );
}

function serializeCartEnvelope(entries: readonly PersistedCartEntry[]) {
  return JSON.stringify({
    storeVersion: CART_STORE_VERSION,
    storeKind: CART_STORE_KIND,
    entries,
  });
}

function parseCartEnvelope(
  rawValue: string | null,
  evaluatedAtEpochMs: number,
):
  | { status: "ready"; entries: readonly PersistedCartEntry[] }
  | { status: "unreadable" } {
  if (rawValue === null) return { status: "ready", entries: [] };
  if (rawValue.length > CART_STORE_MAX_SERIALIZED_CHARACTERS) {
    return { status: "unreadable" };
  }
  let value: unknown;
  try {
    value = JSON.parse(rawValue) as unknown;
  } catch {
    return { status: "unreadable" };
  }
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["storeVersion", "storeKind", "entries"]) ||
    value.storeVersion !== CART_STORE_VERSION ||
    value.storeKind !== CART_STORE_KIND ||
    !Array.isArray(value.entries) ||
    value.entries.length > SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_MAX_ENTRIES
  ) {
    return { status: "unreadable" };
  }
  const draftIds = new Set<string>();
  const entries: PersistedCartEntry[] = [];
  for (const entry of value.entries) {
    if (
      !isRecord(entry) ||
      !hasExactKeys(entry, ["draftId", "savedAtEpochMs", "draft"]) ||
      !isSpecialistLocationGatewayIdentifier(entry.draftId) ||
      draftIds.has(entry.draftId) ||
      !Number.isSafeInteger(entry.savedAtEpochMs) ||
      Number(entry.savedAtEpochMs) < 0 ||
      Number(entry.savedAtEpochMs) >
        evaluatedAtEpochMs + CART_STORE_MAX_SAVED_AT_CLOCK_SKEW_MS ||
      evaluatedAtEpochMs - Number(entry.savedAtEpochMs) >
        SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_MAX_TTL_MS
    ) {
      return { status: "unreadable" };
    }
    const validation =
      validateSpecialistLocationGatewayConfiguredServicesCart(entry.draft);
    if (
      !validation.valid ||
      validation.draft.draftId !== entry.draftId ||
      !exact(validation.draft, entry.draft)
    ) {
      return { status: "unreadable" };
    }
    draftIds.add(entry.draftId);
    entries.push({
      draftId: entry.draftId,
      savedAtEpochMs: entry.savedAtEpochMs as number,
      draft: validation.draft,
    });
  }
  return { status: "ready", entries };
}

function safeEpoch(value: number | undefined) {
  try {
    const epoch = value ?? Date.now();
    return Number.isSafeInteger(epoch) && epoch >= 0 ? epoch : null;
  } catch {
    return null;
  }
}

function exact(left: unknown, right: unknown) {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

function isAmbiguous(status: string) {
  return (
    status === "unreadable" ||
    status === "persistence-failed" ||
    status === "readback-mismatch"
  );
}

function inspectStrict(draftId: string, evaluatedAtEpochMs: number) {
  try {
    return cartStore.inspectPersistedDraft(draftId, evaluatedAtEpochMs);
  } catch {
    return { status: "unreadable" } as const;
  }
}

type CapacityCreateResult =
  | {
      status: "created";
      cart: SpecialistLocationGatewayConfiguredServicesCartV1;
      acceptedRawValue: string;
    }
  | { status: "capacity" | "conflict" | "owner-drift" | "ambiguous" };

/**
 * The generic store bounds physical rows by saved-at age. Cart authority is
 * shorter-lived: a structurally valid row whose own `expiresAtEpochMs` has
 * elapsed must not occupy one of the 16 live authority slots. Capacity
 * reclamation and new publication therefore share one raw CAS. Live siblings
 * survive byte-for-byte as validated values; malformed envelopes are never
 * normalized into an empty store.
 */
function createAfterSemanticCartExpiryPrune(input: {
  cart: SpecialistLocationGatewayConfiguredServicesCartV1;
  lease: NonNullable<
    ReturnType<typeof beginSpecialistLocationGatewayCustomerOwnedWrite>
  >;
  nowEpochMs: number;
}): CapacityCreateResult {
  let storage: Storage;
  try {
    storage = window.sessionStorage;
  } catch {
    return { status: "ambiguous" };
  }
  if (!specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease)) {
    return { status: "owner-drift" };
  }
  const stableRaw = readCustomerOwnedSessionStorageRawValueStable({
    storage,
    storageKey: SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_STORAGE_KEY,
  });
  if (
    stableRaw.status !== "ready" ||
    !specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease)
  ) {
    return specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease)
      ? { status: "ambiguous" }
      : { status: "owner-drift" };
  }
  const parsed = parseCartEnvelope(stableRaw.rawValue, input.nowEpochMs);
  if (parsed.status !== "ready") return { status: "ambiguous" };
  if (parsed.entries.some(({ draftId }) => draftId === input.cart.draftId)) {
    return { status: "conflict" };
  }
  const liveEntries = parsed.entries.filter(
    ({ draft }) => draft.expiresAtEpochMs > input.nowEpochMs,
  );
  if (
    liveEntries.length >=
    SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_MAX_ENTRIES
  ) {
    return { status: "capacity" };
  }
  const nextEntries: readonly PersistedCartEntry[] = [
    {
      draftId: input.cart.draftId,
      savedAtEpochMs: input.nowEpochMs,
      draft: input.cart,
    },
    ...liveEntries,
  ];
  const nextRawValue = serializeCartEnvelope(nextEntries);
  const targetFreeRawValue =
    liveEntries.length === 0 ? null : serializeCartEnvelope(liveEntries);
  if (
    nextRawValue.length > CART_STORE_MAX_SERIALIZED_CHARACTERS ||
    (targetFreeRawValue !== null &&
      targetFreeRawValue.length > CART_STORE_MAX_SERIALIZED_CHARACTERS)
  ) {
    return { status: "ambiguous" };
  }
  const mutationProof = captureCustomerOwnedSessionStorageMutationProof();
  const mutation = commitCustomerOwnedSessionStorageMutation({
    storage,
    storageKey: SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_STORAGE_KEY,
    expectedCurrentRawValue: stableRaw.rawValue,
    nextRawValue,
    staleCleanupRawValue: targetFreeRawValue,
    mutationIsCurrent: () =>
      specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease),
    mutationProof,
  });
  if (!specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease)) {
    return { status: "owner-drift" };
  }
  if (mutation.status === "superseded") return { status: "conflict" };
  if (mutation.status !== "committed") return { status: "ambiguous" };
  const confirmed =
    readCustomerOwnedSessionStorageRawValueFromCapturedReader({
      storage,
      storageKey: SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_STORAGE_KEY,
    });
  if (
    confirmed.status !== "ready" ||
    confirmed.rawValue !== nextRawValue ||
    !customerOwnedSessionStorageMutationProofIsCurrent(mutationProof) ||
    !specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease)
  ) {
    return specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease)
      ? { status: "ambiguous" }
      : { status: "owner-drift" };
  }
  return {
    status: "created",
    cart: input.cart,
    acceptedRawValue: nextRawValue,
  };
}

export type ReadSpecialistLocationGatewayConfiguredServicesCartResult =
  | {
      status: "ready";
      cart: SpecialistLocationGatewayConfiguredServicesCartV1;
    }
  | {
      status: "unavailable";
      reason:
        | "customer-owner-mismatch"
        | "expired"
        | "missing"
        | "owner-mismatch"
        | "unreadable";
    };

export type RetireSpecialistLocationGatewayConfiguredServicesCartsResult =
  | { status: "retired"; retiredCount: number }
  | { status: "absent" }
  | { status: "invalid" | "owner-drift" }
  | { status: "recovery-required"; reason: "ambiguous-persistence" };

/**
 * One atomic intent-scoped drain. It is used when selected-B changes, when a
 * pre-draft closes, and by durable post-commit recovery. A caller may preserve
 * the exact current cart while removing every superseded B publication.
 */
export function retireSpecialistLocationGatewayConfiguredServicesCartsForIntentStrict(input: {
  intentDraftId: string;
  preserveDraftId?: string | null;
  isCurrentOwner: () => boolean;
  nowEpochMs?: number;
}): RetireSpecialistLocationGatewayConfiguredServicesCartsResult {
  const nowEpochMs = safeEpoch(input.nowEpochMs);
  if (
    nowEpochMs === null ||
    !isSpecialistLocationGatewayIdentifier(input.intentDraftId) ||
    (input.preserveDraftId !== undefined &&
      input.preserveDraftId !== null &&
      !isSpecialistLocationGatewayIdentifier(input.preserveDraftId))
  ) {
    return { status: "invalid" };
  }
  const lease = beginSpecialistLocationGatewayCustomerOwnedWrite(
    `configured-services-cart-intent:${input.intentDraftId}`,
    input.isCurrentOwner,
  );
  if (!lease) return { status: "owner-drift" };
  try {
    if (
      !specialistLocationGatewayCustomerOwnedWriteIsCurrent(
        lease,
        input.isCurrentOwner,
      )
    ) {
      return { status: "owner-drift" };
    }
    let storage: Storage;
    try {
      storage = window.sessionStorage;
    } catch {
      quarantineCustomerOwnedSessionStorage();
      return {
        status: "recovery-required",
        reason: "ambiguous-persistence",
      };
    }
    const stable = readCustomerOwnedSessionStorageRawValueStable({
      storage,
      storageKey: SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_STORAGE_KEY,
    });
    if (stable.status !== "ready") {
      quarantineCustomerOwnedSessionStorage();
      return {
        status: "recovery-required",
        reason: "ambiguous-persistence",
      };
    }
    const parsed = parseCartEnvelope(stable.rawValue, nowEpochMs);
    if (parsed.status !== "ready") {
      quarantineCustomerOwnedSessionStorage();
      return {
        status: "recovery-required",
        reason: "ambiguous-persistence",
      };
    }
    const retained = parsed.entries.filter(
      ({ draft }) =>
        draft.intentDraftId !== input.intentDraftId ||
        draft.draftId === input.preserveDraftId,
    );
    const retiredCount = parsed.entries.length - retained.length;
    if (retiredCount === 0) return { status: "absent" };
    const nextRawValue = serializeCartEnvelope(retained);
    const proof = captureCustomerOwnedSessionStorageMutationProof();
    const mutation = commitCustomerOwnedSessionStorageMutation({
      storage,
      storageKey: SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_STORAGE_KEY,
      expectedCurrentRawValue: stable.rawValue,
      nextRawValue,
      staleCleanupRawValue: nextRawValue,
      mutationIsCurrent: () =>
        specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(lease),
      mutationProof: proof,
    });
    if (!specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(lease)) {
      return { status: "owner-drift" };
    }
    if (mutation.status !== "committed") {
      return mutation.status === "superseded"
        ? { status: "owner-drift" }
        : {
            status: "recovery-required",
            reason: "ambiguous-persistence",
          };
    }
    const accepted =
      readCustomerOwnedSessionStorageRawValueFromCapturedReader({
        storage,
        storageKey: SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_STORAGE_KEY,
      });
    if (
      accepted.status !== "ready" ||
      !customerOwnedSessionStorageMutationProofIsCurrent(proof)
    ) {
      quarantineCustomerOwnedSessionStorage();
      return {
        status: "recovery-required",
        reason: "ambiguous-persistence",
      };
    }
    if (accepted.rawValue !== nextRawValue) {
      return { status: "owner-drift" };
    }
    // Span the final caller-owned owner callback with a fresh raw/proof
    // frontier. A callback may synchronously install C while still returning
    // true; equal pre-callback bytes therefore cannot authorize stale success.
    const frontierProof = captureCustomerOwnedSessionStorageMutationProof();
    const finalOwnerIsCurrent =
      specialistLocationGatewayCustomerOwnedWriteIsCurrent(
        lease,
        input.isCurrentOwner,
      );
    const finalRaw =
      readCustomerOwnedSessionStorageRawValueFromCapturedReader({
        storage,
        storageKey: SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_STORAGE_KEY,
      });
    if (!specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(lease)) {
      return { status: "owner-drift" };
    }
    if (!finalOwnerIsCurrent) return { status: "owner-drift" };
    if (
      finalRaw.status === "ready" &&
      finalRaw.rawValue === accepted.rawValue &&
      customerOwnedSessionStorageMutationProofIsCurrent(frontierProof)
    ) {
      return { status: "retired", retiredCount };
    }
    if (
      finalRaw.status === "ready" &&
      (finalRaw.rawValue !== accepted.rawValue ||
        !customerOwnedSessionStorageMutationProofIsCurrent(frontierProof))
    ) {
      return { status: "owner-drift" };
    }
    quarantineCustomerOwnedSessionStorage();
    return {
      status: "recovery-required",
      reason: "ambiguous-persistence",
    };
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return {
      status: "recovery-required",
      reason: "ambiguous-persistence",
    };
  } finally {
    completeSpecialistLocationGatewayCustomerOwnedWrite(lease);
  }
}

export function clearSpecialistLocationGatewayConfiguredServicesCarts() {
  return cartStore.clearDrafts();
}

/**
 * Storage-only hydration. The route may carry the opaque `draftId`, but it
 * cannot reconstruct any line/configuration/mapping bytes from the URL.
 */
export function readSpecialistLocationGatewayConfiguredServicesCartStrict(input: {
  draftId: string;
  expectedOwnerKey: string;
  expectedCustomerAccountId: string | null;
  expectedIntentDraftId: string;
  expectedResolutionId: string;
  nowEpochMs?: number;
}): ReadSpecialistLocationGatewayConfiguredServicesCartResult {
  const nowEpochMs = safeEpoch(input.nowEpochMs);
  if (
    nowEpochMs === null ||
    !isSpecialistLocationGatewayIdentifier(input.draftId) ||
    typeof input.expectedOwnerKey !== "string" ||
    input.expectedOwnerKey.length === 0 ||
    input.expectedOwnerKey.length > 120_000 ||
    (input.expectedCustomerAccountId !== null &&
      !isSpecialistLocationGatewayIdentifier(
        input.expectedCustomerAccountId,
      )) ||
    !isSpecialistLocationGatewayIdentifier(input.expectedIntentDraftId) ||
    !isSpecialistLocationGatewayIdentifier(input.expectedResolutionId)
  ) {
    return { status: "unavailable", reason: "unreadable" };
  }
  const inspected = inspectStrict(input.draftId, nowEpochMs);
  if (inspected.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unavailable", reason: "unreadable" };
  }
  if (inspected.status === "absent") {
    return { status: "unavailable", reason: "missing" };
  }
  const cart = inspected.draft;
  if (!isSpecialistLocationGatewayConfiguredServicesCartFresh(cart, nowEpochMs)) {
    return { status: "unavailable", reason: "expired" };
  }
  if (cart.customerAccountId !== input.expectedCustomerAccountId) {
    return { status: "unavailable", reason: "customer-owner-mismatch" };
  }
  if (
    cart.ownerKey !== input.expectedOwnerKey ||
    cart.intentDraftId !== input.expectedIntentDraftId ||
    cart.resolutionId !== input.expectedResolutionId
  ) {
    return { status: "unavailable", reason: "owner-mismatch" };
  }
  return { status: "ready", cart };
}

export type MutateSpecialistLocationGatewayConfiguredServicesCartResult =
  | {
      status: "published" | "replaced" | "removed";
      cart: SpecialistLocationGatewayConfiguredServicesCartV1;
    }
  | { status: "absent" }
  | {
      status: "conflict";
      reason:
        | "not-a-successor"
        | "target-mismatch"
        | "target-present";
    }
  | { status: "capacity" }
  | { status: "invalid" | "owner-drift" }
  | { status: "recovery-required"; reason: "ambiguous-persistence" };

type CustomerOwnedMutationInput = {
  isCurrentOwner: () => boolean;
  nowEpochMs?: number;
};

function finalOwnerProofOrCompensate(input: {
  cart: SpecialistLocationGatewayConfiguredServicesCartV1;
  lease: NonNullable<
    ReturnType<typeof beginSpecialistLocationGatewayCustomerOwnedWrite>
  >;
  isCurrentOwner: () => boolean;
  nowEpochMs: number;
  acceptedRawValue?: string;
}) {
  let storage: Storage;
  try {
    storage = window.sessionStorage;
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return "ambiguous" as const;
  }
  const rawBeforeOwner =
    readCustomerOwnedSessionStorageRawValueFromCapturedReader({
      storage,
      storageKey: SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_STORAGE_KEY,
    });
  if (rawBeforeOwner.status !== "ready") {
    quarantineCustomerOwnedSessionStorage();
    return "ambiguous" as const;
  }
  if (
    input.acceptedRawValue !== undefined &&
    rawBeforeOwner.rawValue !== input.acceptedRawValue
  ) {
    return "conflict" as const;
  }
  // This proof spans the only remaining caller-owned callback. Equal raw
  // bytes are insufficient by themselves: a registered ABA writer must also
  // prevent this operation from returning stale success.
  const frontierProof = captureCustomerOwnedSessionStorageMutationProof();
  const ownerIsCurrent = specialistLocationGatewayCustomerOwnedWriteIsCurrent(
    input.lease,
    input.isCurrentOwner,
  );
  const rawAfterOwner =
    readCustomerOwnedSessionStorageRawValueFromCapturedReader({
      storage,
      storageKey: SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_STORAGE_KEY,
    });
  if (
    ownerIsCurrent &&
    rawAfterOwner.status === "ready" &&
    rawAfterOwner.rawValue === rawBeforeOwner.rawValue &&
    customerOwnedSessionStorageMutationProofIsCurrent(frontierProof) &&
    specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease)
  ) {
    return "current" as const;
  }
  if (!specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease)) {
    // Teardown advanced the epoch before its own store drain. The cart is
    // non-actionable immediately; the teardown transaction owns its removal.
    return "owner-drift" as const;
  }
  if (
    !ownerIsCurrent &&
    !customerOwnedSessionStorageMutationProofIsCurrent(frontierProof)
  ) {
    return "owner-drift" as const;
  }
  if (
    ownerIsCurrent &&
    rawAfterOwner.status === "ready" &&
    (rawAfterOwner.rawValue !== rawBeforeOwner.rawValue ||
      !customerOwnedSessionStorageMutationProofIsCurrent(frontierProof))
  ) {
    // A re-entrant same-ID publication wins byte-exact. Never remove it as
    // stale-owner compensation or return the superseded cart as actionable.
    return "conflict" as const;
  }
  const proof = captureCustomerOwnedSessionStorageMutationProof();
  const removed = cartStore.removeDraftStrictPersisted(input.cart, {
    evaluatedAtEpochMs: input.nowEpochMs,
    mutationIsCurrent: () =>
      specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease),
    mutationProof: proof,
  });
  if (
    (removed.status === "removed" || removed.status === "absent") &&
    customerOwnedSessionStorageMutationProofIsCurrent(proof)
  ) {
    return "owner-drift" as const;
  }
  if (removed.status === "conflict") {
    // A newer same-ID winner is never removed as compensation.
    return "owner-drift" as const;
  }
  quarantineCustomerOwnedSessionStorage();
  return "ambiguous" as const;
}

/**
 * Removal accepts only the exact target-free envelope produced by strict CAS.
 * That raw value and one mutation-generation proof span the final external
 * owner callback. No compensation write follows this frontier: teardown or a
 * nested C winner already owns the newer bytes and must be preserved.
 */
function finalizeTargetFreeRemoval(input: {
  removedCart: SpecialistLocationGatewayConfiguredServicesCartV1;
  lease: NonNullable<
    ReturnType<typeof beginSpecialistLocationGatewayCustomerOwnedWrite>
  >;
  isCurrentOwner: () => boolean;
  nowEpochMs: number;
}) {
  let storage: Storage;
  try {
    storage = window.sessionStorage;
  } catch {
    return "ambiguous" as const;
  }
  const acceptedRaw =
    readCustomerOwnedSessionStorageRawValueFromCapturedReader({
      storage,
      storageKey: SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_STORAGE_KEY,
    });
  if (acceptedRaw.status !== "ready") return "ambiguous" as const;
  const parsed = parseCartEnvelope(acceptedRaw.rawValue, input.nowEpochMs);
  if (
    parsed.status !== "ready" ||
    parsed.entries.some(
      ({ draftId }) => draftId === input.removedCart.draftId,
    )
  ) {
    return "ambiguous" as const;
  }
  const frontierProof = captureCustomerOwnedSessionStorageMutationProof();
  const ownerIsCurrent = specialistLocationGatewayCustomerOwnedWriteIsCurrent(
    input.lease,
    input.isCurrentOwner,
  );
  const finalRaw =
    readCustomerOwnedSessionStorageRawValueFromCapturedReader({
      storage,
      storageKey: SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_STORAGE_KEY,
    });
  if (!specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease)) {
    return "owner-drift" as const;
  }
  if (!ownerIsCurrent) return "owner-drift" as const;
  if (
    finalRaw.status === "ready" &&
    finalRaw.rawValue === acceptedRaw.rawValue &&
    customerOwnedSessionStorageMutationProofIsCurrent(frontierProof) &&
    specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(input.lease)
  ) {
    return "current" as const;
  }
  if (
    finalRaw.status === "ready" &&
    (finalRaw.rawValue !== acceptedRaw.rawValue ||
      !customerOwnedSessionStorageMutationProofIsCurrent(frontierProof))
  ) {
    return "conflict" as const;
  }
  return "ambiguous" as const;
}

export function publishSpecialistLocationGatewayConfiguredServicesCartStrict(
  input: {
    cart: SpecialistLocationGatewayConfiguredServicesCartV1;
  } & CustomerOwnedMutationInput,
): MutateSpecialistLocationGatewayConfiguredServicesCartResult {
  const validation = validateSpecialistLocationGatewayConfiguredServicesCart(
    input.cart,
  );
  const nowEpochMs = safeEpoch(input.nowEpochMs);
  if (
    !validation.valid ||
    validation.draft.revision !== 0 ||
    nowEpochMs === null ||
    !isSpecialistLocationGatewayConfiguredServicesCartFresh(
      validation.draft,
      nowEpochMs,
    )
  ) {
    return { status: "invalid" };
  }
  const cart = validation.draft;
  const lease = beginSpecialistLocationGatewayCustomerOwnedWrite(
    cart.ownerKey,
    input.isCurrentOwner,
  );
  if (!lease) return { status: "owner-drift" };
  try {
    if (
      !specialistLocationGatewayCustomerOwnedWriteIsCurrent(
        lease,
        input.isCurrentOwner,
      )
    ) {
      return { status: "owner-drift" };
    }
    const proof = captureCustomerOwnedSessionStorageMutationProof();
    const created = cartStore.createDraftStrictPersisted(cart, {
      evaluatedAtEpochMs: nowEpochMs,
      mutationIsCurrent: () =>
        specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(lease),
      mutationProof: proof,
    });
    let acceptedCart: SpecialistLocationGatewayConfiguredServicesCartV1;
    let acceptedRawValue: string | undefined;
    if (created.status !== "created") {
      if (
        isAmbiguous(created.status) &&
        !specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(lease)
      ) {
        return { status: "owner-drift" };
      }
      if (isAmbiguous(created.status)) {
        quarantineCustomerOwnedSessionStorage();
        return {
          status: "recovery-required",
          reason: "ambiguous-persistence",
        };
      }
      if (created.status === "conflict") {
        return { status: "conflict", reason: "target-present" };
      }
      if (created.status !== "capacity") return { status: "invalid" };
      const reclaimed = createAfterSemanticCartExpiryPrune({
        cart,
        lease,
        nowEpochMs,
      });
      if (reclaimed.status !== "created") {
        if (reclaimed.status === "owner-drift") {
          return { status: "owner-drift" };
        }
        if (reclaimed.status === "capacity") return { status: "capacity" };
        if (reclaimed.status === "conflict") {
          return { status: "conflict", reason: "target-mismatch" };
        }
        quarantineCustomerOwnedSessionStorage();
        return {
          status: "recovery-required",
          reason: "ambiguous-persistence",
        };
      }
      acceptedCart = reclaimed.cart;
      acceptedRawValue = reclaimed.acceptedRawValue;
    } else {
      acceptedCart = created.draft;
    }
    const final = finalOwnerProofOrCompensate({
      cart: acceptedCart,
      lease,
      isCurrentOwner: input.isCurrentOwner,
      nowEpochMs,
      acceptedRawValue,
    });
    if (final === "current") {
      return { status: "published", cart: acceptedCart };
    }
    if (final === "conflict") {
      return { status: "conflict", reason: "target-mismatch" };
    }
    return final === "owner-drift"
      ? { status: "owner-drift" }
      : {
          status: "recovery-required",
          reason: "ambiguous-persistence",
        };
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  } finally {
    completeSpecialistLocationGatewayCustomerOwnedWrite(lease);
  }
}

export function replaceSpecialistLocationGatewayConfiguredServicesCartStrict(
  input: {
    expected: SpecialistLocationGatewayConfiguredServicesCartV1;
    next: SpecialistLocationGatewayConfiguredServicesCartV1;
  } & CustomerOwnedMutationInput,
): MutateSpecialistLocationGatewayConfiguredServicesCartResult {
  const expectedValidation =
    validateSpecialistLocationGatewayConfiguredServicesCart(input.expected);
  const nextValidation =
    validateSpecialistLocationGatewayConfiguredServicesCart(input.next);
  const nowEpochMs = safeEpoch(input.nowEpochMs);
  if (
    !expectedValidation.valid ||
    !nextValidation.valid ||
    nowEpochMs === null ||
    !isSpecialistLocationGatewayConfiguredServicesCartFresh(
      nextValidation.draft,
      nowEpochMs,
    )
  ) {
    return { status: "invalid" };
  }
  const expected = expectedValidation.draft;
  const next = nextValidation.draft;
  if (!isSpecialistLocationGatewayServicesCartRevisionSuccessor(expected, next)) {
    return { status: "conflict", reason: "not-a-successor" };
  }
  const lease = beginSpecialistLocationGatewayCustomerOwnedWrite(
    expected.ownerKey,
    input.isCurrentOwner,
  );
  if (!lease) return { status: "owner-drift" };
  try {
    if (
      !specialistLocationGatewayCustomerOwnedWriteIsCurrent(
        lease,
        input.isCurrentOwner,
      )
    ) {
      return { status: "owner-drift" };
    }
    const proof = captureCustomerOwnedSessionStorageMutationProof();
    const replaced = cartStore.replaceDraftStrictPersisted(expected, next, {
      evaluatedAtEpochMs: nowEpochMs,
      mutationIsCurrent: () =>
        specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(lease),
      mutationProof: proof,
    });
    if (replaced.status !== "replaced") {
      if (
        isAmbiguous(replaced.status) &&
        !specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(lease)
      ) {
        return { status: "owner-drift" };
      }
      if (isAmbiguous(replaced.status)) {
        quarantineCustomerOwnedSessionStorage();
        return {
          status: "recovery-required",
          reason: "ambiguous-persistence",
        };
      }
      return replaced.status === "conflict"
        ? { status: "conflict", reason: "target-mismatch" }
        : { status: "invalid" };
    }
    const final = finalOwnerProofOrCompensate({
      cart: replaced.draft,
      lease,
      isCurrentOwner: input.isCurrentOwner,
      nowEpochMs,
    });
    if (final === "current") {
      return { status: "replaced", cart: replaced.draft };
    }
    if (final === "conflict") {
      return { status: "conflict", reason: "target-mismatch" };
    }
    return final === "owner-drift"
      ? { status: "owner-drift" }
      : {
          status: "recovery-required",
          reason: "ambiguous-persistence",
        };
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  } finally {
    completeSpecialistLocationGatewayCustomerOwnedWrite(lease);
  }
}

export function removeSpecialistLocationGatewayConfiguredServicesCartStrict(
  input: {
    expected: SpecialistLocationGatewayConfiguredServicesCartV1;
  } & CustomerOwnedMutationInput,
): MutateSpecialistLocationGatewayConfiguredServicesCartResult {
  const validation = validateSpecialistLocationGatewayConfiguredServicesCart(
    input.expected,
  );
  const nowEpochMs = safeEpoch(input.nowEpochMs);
  if (!validation.valid || nowEpochMs === null) return { status: "invalid" };
  const expected = validation.draft;
  const lease = beginSpecialistLocationGatewayCustomerOwnedWrite(
    expected.ownerKey,
    input.isCurrentOwner,
  );
  if (!lease) return { status: "owner-drift" };
  try {
    if (
      !specialistLocationGatewayCustomerOwnedWriteIsCurrent(
        lease,
        input.isCurrentOwner,
      )
    ) {
      return { status: "owner-drift" };
    }
    const proof = captureCustomerOwnedSessionStorageMutationProof();
    const removed = cartStore.removeDraftStrictPersisted(expected, {
      evaluatedAtEpochMs: nowEpochMs,
      mutationIsCurrent: () =>
        specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(lease),
      mutationProof: proof,
    });
    if (removed.status === "removed") {
      if (!exact(removed.draft, expected)) {
        quarantineCustomerOwnedSessionStorage();
        return {
          status: "recovery-required",
          reason: "ambiguous-persistence",
        };
      }
      const final = finalizeTargetFreeRemoval({
        removedCart: expected,
        lease,
        isCurrentOwner: input.isCurrentOwner,
        nowEpochMs,
      });
      if (final === "current") return { status: "removed", cart: expected };
      if (final === "owner-drift") return { status: "owner-drift" };
      if (final === "conflict") {
        return { status: "conflict", reason: "target-mismatch" };
      }
      quarantineCustomerOwnedSessionStorage();
      return {
        status: "recovery-required",
        reason: "ambiguous-persistence",
      };
    }
    if (removed.status === "absent") {
      const final = finalizeTargetFreeRemoval({
        removedCart: expected,
        lease,
        isCurrentOwner: input.isCurrentOwner,
        nowEpochMs,
      });
      if (final === "current") return { status: "absent" };
      if (final === "owner-drift") return { status: "owner-drift" };
      if (final === "conflict") {
        return { status: "conflict", reason: "target-mismatch" };
      }
      quarantineCustomerOwnedSessionStorage();
      return {
        status: "recovery-required",
        reason: "ambiguous-persistence",
      };
    }
    if (removed.status === "conflict") {
      return { status: "conflict", reason: "target-mismatch" };
    }
    if (isAmbiguous(removed.status)) {
      quarantineCustomerOwnedSessionStorage();
      return { status: "recovery-required", reason: "ambiguous-persistence" };
    }
    return { status: "invalid" };
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return { status: "recovery-required", reason: "ambiguous-persistence" };
  } finally {
    completeSpecialistLocationGatewayCustomerOwnedWrite(lease);
  }
}
